import { useEffect } from "react";
import { gql, useMutation } from "@apollo/client";
import CiroModal from "../../../shared/CiroModal";
import {
  AutomationListImportContactsModal_StartImportMutation,
  AutomationListImportContactsModal_StartImportMutationVariables,
} from "../../../../__generated__/graphql";

// Import steps
import UploadStep from "./ImportContactsModalUploadStep";
import MappingStep from "./ImportContactsModalMappingStep";
import ValidationStep from "./ImportContactsModalValidationStep";
import { ImportStepEnum, useImportState } from "./useImportState";

// Define GraphQL mutation to start import job
export const AutomationListImportContactsModal_StartImport = gql`
  mutation AutomationListImportContactsModal_StartImport(
    $listId: Int!
    $contacts: [ContactListContactInput!]!
  ) {
    startImportContactsToList(listId: $listId, contacts: $contacts) {
      success
    }
  }
`;

export interface IAutomationListImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactListId: number;
  onSuccess: () => void;
}

const AutomationListImportContactsModal: React.FC<
  IAutomationListImportContactsModalProps
> = ({ isOpen, onClose, contactListId, onSuccess }) => {
  const importState = useImportState();
  const {
    step,
    setStep,
    setIsProcessing,
    setError,
    subscriptionEnabled,
    setSubscriptionEnabled,
    subscriptionVariables,
  } = importState;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Disable subscription when modal closes
      setSubscriptionEnabled(false);

      setTimeout(() => {
        importState.reset();
      }, 300);
    }
  }, [isOpen, importState, setSubscriptionEnabled]);

  // Handle import job with mutation
  const [startImportMutation] = useMutation<
    AutomationListImportContactsModal_StartImportMutation,
    AutomationListImportContactsModal_StartImportMutationVariables
  >(AutomationListImportContactsModal_StartImport);

  // Start import job when subscription variables are set
  useEffect(() => {
    if (subscriptionEnabled && subscriptionVariables) {
      const startImport = async () => {
        try {
          const result = await startImportMutation({
            variables: subscriptionVariables,
          });

          if (result.data?.startImportContactsToList?.success) {
            // Close modal immediately and let user see progress in list view
            onSuccess();
            onClose();
          } else {
            throw new Error("Failed to start import job");
          }
        } catch (error: any) {
          console.error("Error starting import:", error);
          setError(error.message || "Failed to start import");
          setIsProcessing(false);
          setSubscriptionEnabled(false);
          setStep(ImportStepEnum.VALIDATION);
        }
      };

      startImport();
    }
  }, [
    subscriptionEnabled,
    subscriptionVariables,
    startImportMutation,
    contactListId,
    onSuccess,
    onClose,
    setError,
    setIsProcessing,
    setSubscriptionEnabled,
    setStep,
  ]);

  return (
    <CiroModal size="XLARGE" isOpen={isOpen} onClose={onClose}>
      {step === ImportStepEnum.UPLOAD && (
        <UploadStep importState={importState} onClose={onClose} />
      )}
      {step === ImportStepEnum.MAPPING && (
        <MappingStep importState={importState} />
      )}
      {step === ImportStepEnum.VALIDATION && (
        <ValidationStep
          importState={importState}
          contactListId={contactListId}
        />
      )}
    </CiroModal>
  );
};

export default AutomationListImportContactsModal;
